import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Heart, Shield, Moon, ChevronRight } from "lucide-react";
import { LithaeLogo } from "@/components/LithaeLogo";

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Heart,
      title: "Bien-être émotionnel",
      description: "Découvrez des pierres qui accompagnent votre équilibre émotionnel"
    },
    {
      icon: Shield,
      title: "Protection énergétique",
      description: "Trouvez votre bouclier contre les énergies négatives"
    },
    {
      icon: Moon,
      title: "Harmonie intérieure",
      description: "Rétablissez l&apos;harmonie entre corps, âme et esprit"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="https://www.lithae.com" target="_blank" rel="noopener noreferrer">
              <LithaeLogo size="default" />
            </a>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => navigate('/quiz')}
              className="hidden sm:flex"
            >
              Commencer
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-24 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-8 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-secondary-foreground">
                  Quiz personnalisé
                </span>
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-tight">
                Trouvez la pierre qui vous{" "}
                <span className="text-primary">correspond</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                Répondez à quelques questions et découvrez les pierres naturelles 
                qui accompagneront votre chemin vers le bien-être et l&apos;harmonie intérieure.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="primary" 
                  size="xl"
                  onClick={() => navigate('/quiz')}
                  className="group"
                >
                  Commencer le quiz
                  <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-muted-foreground/80 italic">
                Approche de bien-être, ne remplace pas un avis médical.
              </p>
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-medium animate-fade-in">
                <img 
                  src="https://www.lithae.com/web/image/926-5f126630/magnific-rMoPDnpB5jrpSYn2M5hb-15.webp"
                  alt="Collection de bijoux et pierres naturelles Lithae"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              La lithothérapie à votre service
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Les pierres naturelles sont utilisées depuis des millénaires pour leurs 
              propriétés énergétiques et leur capacité à harmoniser notre bien-être.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-stone bg-card p-8 text-center animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              Comment ça fonctionne
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Répondez au quiz", desc: "Quelques questions sur vos besoins et préférences" },
              { step: "2", title: "Découvrez vos pierres", desc: "Notre algorithme sélectionne les pierres idéales pour vous" },
              { step: "3", title: "Explorez leurs bienfaits", desc: "Apprenez tout sur vos pierres recommandées" }
            ].map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-serif text-xl font-semibold mb-4">
                  {item.step}
                </div>
                <h3 className="font-serif text-lg font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              variant="primary" 
              size="xl"
              onClick={() => navigate('/quiz')}
            >
              Commencer maintenant
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <a href="https://www.lithae.com" target="_blank" rel="noopener noreferrer">
              <LithaeLogo size="small" />
            </a>
            <p className="text-xs text-muted-foreground text-center sm:text-right">
              Approche de bien-être, ne remplace pas un avis médical.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
